import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Shovel, Map, Compass, Tent, Scroll, Hammer, Brush } from 'lucide-react';

const ArchaeologyDig: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Petra Institute";
    
    return (
        <div className="min-h-screen bg-[#f3efe6] font-serif text-[#4a3b32] selection:bg-[#8c735a] selection:text-white border-[16px] border-[#e8e4db] overflow-x-hidden relative">
            
            {/* Texture Overlay */}
            <div className="fixed inset-0 opacity-40 pointer-events-none mix-blend-multiply bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')]"></div>

            {/* Navigation looking like a header on worn paper */}
            <nav className="relative z-10 px-8 py-8 flex justify-between items-center border-b-2 border-[#4a3b32]/10 mx-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#8c735a] rounded-sm flex items-center justify-center text-[#f3efe6] shadow-sm transform -rotate-2">
                        <Scroll size={24} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold text-2xl tracking-wide uppercase">{schoolName}</span>
                        <span className="text-xs font-sans font-bold tracking-[0.2em] text-[#8c735a]">Est. 1895</span>
                    </div>
                </div>

                <div className="hidden md:flex gap-10 font-sans font-bold text-xs uppercase tracking-[0.15em] text-[#5d5248]">
                    <a href="#" className="hover:text-[#8c735a] transition-colors decoration-2 hover:underline underline-offset-4">Expeditions</a>
                    <a href="#" className="hover:text-[#8c735a] transition-colors decoration-2 hover:underline underline-offset-4">Museum</a>
                    <a href="#" className="hover:text-[#8c735a] transition-colors decoration-2 hover:underline underline-offset-4">Journal</a>
                </div>

                <Button className="bg-[#4a3b32] text-[#f3efe6] hover:bg-[#2c231e] font-sans font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-sm shadow-md">
                    Join The Dig
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 px-8 py-20 max-w-5xl mx-auto">
                <div className="border-4 border-double border-[#4a3b32]/20 p-12 bg-[#fdfbf7] shadow-xl rotate-1">
                     <div className="flex justify-center mb-8">
                         <Compass size={48} strokeWidth={1} className="text-[#8c735a]" />
                     </div>
                     <h1 className="text-5xl md:text-7xl font-bold text-center mb-8 leading-tight">
                        Unearthing the <br/>
                        <span className="text-[#8c735a]">Stories</span> of the Past.
                     </h1>
                     <p className="text-center font-sans text-[#5d5248] max-w-lg mx-auto leading-loose text-sm tracking-wide mb-12">
                        We are explorers of history, piecing together the diverse tapestry of human civilization one artifact at a time.
                     </p>
                     
                     <div className="flex justify-center gap-6">
                         <div className="flex flex-col items-center gap-2">
                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#8c735a] flex items-center justify-center hover:bg-[#8c735a] hover:text-white transition-colors cursor-pointer text-[#4a3b32]">
                                <Map size={24} />
                            </div>
                            <span className="font-sans text-[10px] font-bold uppercase tracking-widest">Map</span>
                         </div>
                         <div className="flex flex-col items-center gap-2">
                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#8c735a] flex items-center justify-center hover:bg-[#8c735a] hover:text-white transition-colors cursor-pointer text-[#4a3b32]">
                                <Hammer size={24} />
                            </div>
                             <span className="font-sans text-[10px] font-bold uppercase tracking-widest">Tools</span>
                         </div>
                         <div className="flex flex-col items-center gap-2">
                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#8c735a] flex items-center justify-center hover:bg-[#8c735a] hover:text-white transition-colors cursor-pointer text-[#4a3b32]">
                                <Tent size={24} />
                            </div>
                             <span className="font-sans text-[10px] font-bold uppercase tracking-widest">Camp</span>
                         </div>
                     </div>
                </div>
            </header>

            {/* Field Notes Section */}
            <section className="relative z-10 py-20 px-8">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 item-center">
                    <div className="relative">
                        <div className="absolute inset-0 border-2 border-[#4a3b32] transform translate-x-3 translate-y-3"></div>
                        <div className="relative bg-[#e8e4db] aspect-[4/5] flex items-center justify-center overflow-hidden">
                            <div className="absolute inset-4 border border-dashed border-[#4a3b32]/40"></div>
                            {/* In a real scenario, this would be an image of a dig site */}
                            <div className="text-center p-8">
                                <span className="font-mono text-xs block mb-2">FIG 4.2 - Site A</span>
                                <Brush size={64} className="mx-auto text-[#8c735a] opacity-50" />
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <div className="inline-block border-b-2 border-[#8c735a] mb-6 pb-1 font-sans text-xs font-bold uppercase tracking-widest text-[#8c735a]">
                            Field Notes: Week 12
                        </div>
                        <h2 className="text-4xl font-bold mb-6">Discovery at the Eastern Ridge</h2>
                        <p className="leading-loose mb-6 font-medium text-[#5d5248]">
                            Our team has uncovered a significant settlement dating back to the late Bronze Age. 
                            Initial findings suggest a trade hub connecting the northern tribes with the coastal cities.
                        </p>
                        <p className="leading-loose mb-10 text-[#5d5248]">
                            Students are currently cataloging pottery shards and metalwork found in the lower strata.
                        </p>
                        
                        <Button variant="outline" className="rounded-none border-2 border-[#4a3b32] text-[#4a3b32] font-sans font-bold uppercase tracking-widest hover:bg-[#4a3b32] hover:text-[#f3efe6]">
                            Read Full Report
                        </Button>
                    </div>
                </div>
            </section>
            
            {/* Topographic Lines Decoration (CSS approximation) */}
            <div className="absolute bottom-0 left-0 w-full h-32 opacity-10 pointer-events-none overflow-hidden">
                 <div className="w-[120%] h-[200%] border-t-2 border-[#4a3b32] rounded-[100%] absolute -bottom-[100%] -left-[10%]"></div>
                 <div className="w-[120%] h-[200%] border-t-2 border-[#4a3b32] rounded-[100%] absolute -bottom-[110%] -left-[10%]"></div>
                 <div className="w-[120%] h-[200%] border-t-2 border-[#4a3b32] rounded-[100%] absolute -bottom-[120%] -left-[10%]"></div>
            </div>

             {/* Footer */}
            <footer className="relative z-10 bg-[#2c231e] text-[#d6cfc2] py-20 px-8">
                 <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
                     <div>
                         <h3 className="font-bold text-2xl mb-4 tracking-wide">{schoolName}</h3>
                         <p className="max-w-sm font-sans text-sm leading-relaxed text-[#9e968b]">
                             Preserving heritage through scientific excavation and public education.
                         </p>
                     </div>
                     <div className="grid grid-cols-2 gap-12 font-sans text-xs uppercase tracking-widest text-[#9e968b]">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Volunteers</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Donations</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Press</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Locations</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Legal</a></li>
                         </ul>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default ArchaeologyDig;
